'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

/**
 * Retailers can have multiple allowed support platform host origins set. Because browsers
 * do not allow child windows to access properties of their parent windows, agent portal does
 * not know the url of the platform that's hosting it. So the alternative to firing the message
 * for only one known, supported origin is to fire the message for all supported origins.
 */
function sendIframeMessage(options) {
  if (!options.origins) throw new Error('No origin specified');
  options.origins.forEach(origin => {
    try {
      window.parent.postMessage(options.message, origin);
    } catch (e) {
      // TODO: Think of a better way to handle this messaging. We likely don't want to treat
      // an error from window.postMessage as an error. One example is supportPlatformOrigins has
      // many valid origins, one of which matches the parent origin, but also one malformed origin.
      // We don't want to error out for that one bad origin when we also have the correct origin.
      // eslint-disable-next-line no-console
      console.warn(e);
    }
  });
}

/**
 * Since retailers can have multiple origins configured, when agent portal receives a message
 * it needs to go through the allowed list of support platform origins to determine if it's
 * safe to act on the message.
 */
function isAllowedMessageOrigin(options) {
  var _options$origins;
  return (_options$origins = options.origins) === null || _options$origins === void 0 ? void 0 : _options$origins.includes(options.event.origin);
}

/**
 * This function wraps a request / response kind of iframe messaging into a "query". It adds
 * a listener for the response message, sends the request message, and then resolves with
 * the response message from the parent iframe.
 */
function iframeQuery(options) {
  const {
    requestMessage,
    timeout = 5000,
    responseMessageSchema,
    origins
  } = options;
  if (!origins) return Promise.reject(new Error('MISSING_ORIGIN'));
  const iframeResponse = new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('TIMEOUT'));
    }, timeout);
    function handleReceiveMessage(event) {
      if (!isAllowedMessageOrigin({
        origins,
        event
      })) {
        return;
      }
      const response = responseMessageSchema.safeParse(event.data);
      if (response.success) {
        clearTimeout(timer);
        removeEventListener('message', handleReceiveMessage);
        resolve(response.data);
      }
    }
    window.addEventListener('message', handleReceiveMessage, false);
  });

  // Retailers can have the agent portal configured for multiple
  sendIframeMessage({
    origins,
    message: requestMessage
  });
  return iframeResponse;
}

exports.iframeQuery = iframeQuery;
exports.isAllowedMessageOrigin = isAllowedMessageOrigin;
exports.sendIframeMessage = sendIframeMessage;
